/**
 * Pure model for the Commissioner's score correction review (ISSUE-1325,
 * SUR-R62a). A supplier corrected a score after a contest was confirmed; the
 * server works out what that WOULD change (`/contest/reopen-preview-get`) and
 * the Commissioner either applies it or keeps the confirmed result.
 *
 * Every number here is the server's preview. This module only turns it into
 * sentences, so nothing on the client re-grades a round.
 *
 * No React, no network, no SDK value import.
 */

export const CORRECTION_TITLE = "A score changed after this contest ended";
export const CORRECTION_LOADING_LABEL = "Loading the score correction";
export const CORRECTION_APPLY_LABEL = "Apply the correction";
export const CORRECTION_APPLYING_LABEL = "Applying the correction";
export const CORRECTION_KEEP_LABEL = "Keep the results as they are";
export const CORRECTION_KEEPING_LABEL = "Keeping the results";
export const CORRECTION_APPLY_ANYWAY_LABEL = "Apply these changes";
export const CORRECTION_CHANGES_TITLE = "What would change";
export const CORRECTION_NO_ENTRY_CHANGES =
  "No player's status or points would change.";
export const CORRECTION_WINNERS_SAME = "The winners stay the same.";
export const CORRECTION_WINNERS_NONE = "Nobody";

export const CORRECTION_APPLY_CONFIRM_TITLE = "Apply this score correction?";
export const CORRECTION_APPLY_CONFIRM_BODY =
  "Standings, medals and winners change to match the corrected score, and everyone in the pool sees the new result.";
export const CORRECTION_KEEP_CONFIRM_TITLE = "Keep the confirmed results?";
export const CORRECTION_KEEP_CONFIRM_BODY =
  "The corrected score is set aside for this contest and the result everyone has already seen stands.";
export const CORRECTION_KEEP_CONFIRM_LABEL = "Keep results";
export const CORRECTION_CONFIRM_CANCEL_LABEL = "Go back";

export const CORRECTION_NEEDS_EDIT_NOTE =
  "This correction changes a player who is already out, so it cannot be applied on its own. Check the changes above and apply them yourself, or keep the results as they are.";
export const CORRECTION_MOVED_NOTE =
  "The score changed again while you were looking. Here is the latest.";
export const CORRECTION_APPLY_ANYWAY_CONFIRM_TITLE =
  "Apply these changes by hand?";
export const CORRECTION_APPLY_ANYWAY_CONFIRM_BODY =
  "Every player listed above is set to the new status, lives and points shown, including anyone who is out, and the winners change to match.";

export type typeCorrectionStatus = "in" | "eliminated" | "revived";

export type typeCorrectionAffectedEntry = {
  entryId: string;
  userId: string;
  displayName: string;
  currentStatus: typeCorrectionStatus;
  proposedStatus: typeCorrectionStatus;
  currentLivesRemaining: number;
  proposedLivesRemaining: number;
  currentPointsTotal: number;
  proposedPointsTotal: number;
};

export type typeCorrectionPreview = {
  roundNumber: number;
  refusalReason:
    "laterRoundOpened" | "resultSignedOff" | "entryRevivedFromRound";
  previewResultsDigest: string;
  affectedEntries: readonly typeCorrectionAffectedEntry[];
  currentWinnerEntryIds: readonly string[];
  proposedWinnerEntryIds: readonly string[];
  seasonTrophy: {
    applies: boolean;
    proposedHolderUserIds: readonly string[];
  };
};

function statusWord(status: typeCorrectionStatus): string {
  if (status === "in") return "In";
  if (status === "revived") return "Revived";
  return "Eliminated";
}

/** Why the server did not just apply the correction itself. */
export function correctionIntroLine(input: {
  roundNumber: number;
  refusalReason: typeCorrectionPreview["refusalReason"];
}): string {
  const round = `Round ${String(input.roundNumber)}`;
  if (input.refusalReason === "laterRoundOpened") {
    return `The league corrected a score in ${round} after a later round had already started, so it needs your call.`;
  }
  if (input.refusalReason === "entryRevivedFromRound") {
    return `The league corrected a score in ${round} after someone was revived from it, so it needs your call.`;
  }
  return `The league corrected a score in ${round} after you confirmed the result, so it needs your call.`;
}

/** One line per player the correction would touch, naming only what moves. */
export function affectedEntryLine(entry: typeCorrectionAffectedEntry): string {
  const parts: string[] = [];
  if (entry.currentStatus !== entry.proposedStatus) {
    parts.push(
      `${statusWord(entry.currentStatus)} to ${statusWord(entry.proposedStatus)}`,
    );
  }
  if (entry.currentLivesRemaining !== entry.proposedLivesRemaining) {
    parts.push(
      `lives ${String(entry.currentLivesRemaining)} to ${String(entry.proposedLivesRemaining)}`,
    );
  }
  if (entry.currentPointsTotal !== entry.proposedPointsTotal) {
    parts.push(
      `points ${String(entry.currentPointsTotal)} to ${String(entry.proposedPointsTotal)}`,
    );
  }
  const change = parts.length === 0 ? "no change" : parts.join(", ");
  return `${entry.displayName}: ${change}`;
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
}

function namesFor(
  entryIds: readonly string[],
  namesByEntryId: ReadonlyMap<string, string>,
): string {
  if (entryIds.length === 0) return CORRECTION_WINNERS_NONE;
  return entryIds
    .map((entryId) => namesByEntryId.get(entryId) ?? "A player")
    .join(", ");
}

/** "The winners stay the same." or "Winner: Sam, would be: Jo." */
export function winnersLine(input: {
  current: readonly string[];
  proposed: readonly string[];
  namesByEntryId: ReadonlyMap<string, string>;
}): string {
  if (sameSet(input.current, input.proposed)) return CORRECTION_WINNERS_SAME;
  return `Winners now: ${namesFor(input.current, input.namesByEntryId)}. After the correction: ${namesFor(input.proposed, input.namesByEntryId)}.`;
}

/**
 * The hand-applied version (`editAndApply`) for a correction the server will
 * not apply on its own. It is the server's own proposed numbers, unchanged:
 * the Commissioner has just read every one of them on the card.
 */
export function buildEditAndApplyOverride(preview: typeCorrectionPreview): {
  entries: {
    entryId: string;
    status: typeCorrectionStatus;
    pointsTotal: number;
    livesRemaining: number;
  }[];
  winnerEntryIds: string[];
  seasonTrophyHolderUserIds: string[] | null;
} {
  return {
    entries: preview.affectedEntries.map((entry) => ({
      entryId: entry.entryId,
      status: entry.proposedStatus,
      pointsTotal: entry.proposedPointsTotal,
      livesRemaining: entry.proposedLivesRemaining,
    })),
    winnerEntryIds: [...preview.proposedWinnerEntryIds],
    seasonTrophyHolderUserIds: preview.seasonTrophy.applies
      ? [...preview.seasonTrophy.proposedHolderUserIds]
      : null,
  };
}

/**
 * What a refused resolve means on the card. Two refusals are answered in
 * place; every other one goes to the shared error modal.
 */
export function resolveRefusalTreatment(
  error: { code?: string; details?: Record<string, unknown> } | null,
): "needsEdit" | "moved" | "modal" {
  const reason =
    typeof error?.details?.reason === "string" ? error.details.reason : null;
  if (error?.code === "CONFLICT" && reason === "useEditAndApply") {
    return "needsEdit";
  }
  if (error?.code === "CONFLICT" && reason === "correctionMoved") {
    return "moved";
  }
  return "modal";
}

/**
 * A reopen refused because the contest is already reopened is the review
 * picking up where it left off (a reload between reopen and resolve, or a
 * co-Commissioner who opened it first), not a failure. The server answers
 * that case `raceLost`. Resolve still checks everything itself, so going on
 * to it is safe. Every other refusal is a real one.
 */
export function isAlreadyReopened(
  error: { code?: string; details?: Record<string, unknown> } | null,
): boolean {
  return error?.code === "CONFLICT" && error.details?.reason === "raceLost";
}
